import React, { useEffect, useMemo, useState } from 'react';

import {
  geoArea,
  geoBounds,
  geoCentroid,
  geoCircle,
  geoContains,
  geoNaturalEarth1,
  geoPath,
} from 'd3-geo';
import type { Feature, FeatureCollection, Geometry, Polygon } from 'geojson';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { feature } from 'topojson-client';
import type { GeometryCollection, Topology } from 'topojson-specification';

import {
  MemberCoursesPlayedSummaryDTO,
  MemberHomeCourseSummaryDTO,
} from '../../services/apiClient';

// The world's countries, Natural Earth 1:50m via world-atlas (licence beside the file). Served from
// public/ rather than bundled, so only the profile ever downloads it, once, and the browser keeps
// it. 50m and not the lighter 110m: 110m leaves out small countries like Mauritius and Singapore,
// and a member who played there would see nothing fill in.
const WORLD_URL = '/geo/countries-50m.json';

type Country = Feature<Geometry, { name?: string }>;

// One fetch per page load, shared by every map that mounts.
let worldPromise: Promise<Country[]> | null = null;
const loadWorld = (): Promise<Country[]> => {
  worldPromise ??= fetch(WORLD_URL)
    .then((response) => {
      if (!response.ok) {
        throw new Error(`World map ${response.status}`);
      }
      return response.json() as Promise<Topology>;
    })
    .then((topology) => {
      const countries = feature(
        topology,
        topology.objects.countries as GeometryCollection<{ name?: string }>,
      ) as FeatureCollection<Geometry, { name?: string }>;
      return countries.features;
    })
    .catch((error) => {
      worldPromise = null; // Let the next mount try again.
      throw error;
    });
  return worldPromise;
};

// "Côte d'Ivoire" and "Cote d'Ivoire", "Bosnia and Herz." and "Bosnia and Herzegovina": the
// comparison ignores accents, case and punctuation. What still differs is caught by the point test.
const normalise = (name: string): string =>
  name
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z]/g, '');

interface Point {
  longitude: number;
  latitude: number;
}

// Which country a point is in. The bounding box is checked first because it is cheap, and
// geoContains is not. A coastal course can sit just off the 1:50m coastline, so a miss is normal.
const countryAt = (
  countries: Country[],
  boxes: [[number, number], [number, number]][],
  point: Point,
): number => {
  const { longitude, latitude } = point;
  return countries.findIndex((country, index) => {
    const [[west, south], [east, north]] = boxes[index];
    const inLongitude =
      west <= east
        ? longitude >= west && longitude <= east
        : longitude >= west || longitude <= east; // Crosses the 180th meridian.
    return (
      inLongitude &&
      latitude >= south &&
      latitude <= north &&
      geoContains(country, [longitude, latitude])
    );
  });
};

const pointOf = (row: {
  courseLatitude?: number;
  courseLongitude?: number;
}): Point | null =>
  row.courseLatitude != null && row.courseLongitude != null
    ? { latitude: row.courseLatitude, longitude: row.courseLongitude }
    : null;

// A country's main land, for framing only. Many countries carry far-off islands in the same shape
// (South Africa's Prince Edward Islands, France's French Guiana), and fitting the view to those
// would leave the country itself a small shape in one corner. The islands are still drawn gold.
const mainland = (country: Country): Feature => {
  const { geometry } = country;
  if (geometry.type !== 'MultiPolygon') {
    return country;
  }
  let largest: Polygon | null = null;
  let largestArea = -1;
  geometry.coordinates.forEach((coordinates) => {
    const polygon: Polygon = { type: 'Polygon', coordinates };
    const area = geoArea(polygon);
    if (area > largestArea) {
      largest = polygon;
      largestArea = area;
    }
  });
  return largest != null
    ? { type: 'Feature', properties: {}, geometry: largest }
    : country;
};

// The viewBox. 2:1 suits a world map and still frames one country well once it is zoomed in.
const WIDTH = 800;
const HEIGHT = 400;
const PADDING = 24;
// Never zoom in closer than a circle this many degrees across, so a small country (or a single
// home course) still shows its neighbours and reads as a place on a map.
const MIN_VIEW_DEGREES = 12;

interface PlayedCountriesMapProps {
  coursesPlayed: MemberCoursesPlayedSummaryDTO[];
  homeCourses: MemberHomeCourseSummaryDTO[];
}

// The profile's picture of where a member has played: every country they have played a course
// in, filled gold, zoomed to fit them. Not a live map (Explore is the live map, and this opens it
// with the Played filter on), so it is cheap on a phone and reads the same for 1 course or 6,000.
// With nothing played yet it shows the country of their first home course instead.
//
// Countries are matched by name first, then, for a name the world file spells differently, by
// testing a course's own coordinates against the country shapes.
const PlayedCountriesMap: React.FC<PlayedCountriesMapProps> = ({
  coursesPlayed,
  homeCourses,
}) => {
  const [countries, setCountries] = useState<Country[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    loadWorld()
      .then((loaded) => !cancelled && setCountries(loaded))
      .catch(() => !cancelled && setFailed(true));
    return (): void => {
      cancelled = true;
    };
  }, []);

  const map = useMemo(() => {
    if (countries == null) {
      return null;
    }

    const boxes = countries.map((country) => geoBounds(country));
    const byName = new Map<string, number>();
    countries.forEach((country, index) => {
      if (country.properties?.name) {
        byName.set(normalise(country.properties.name), index);
      }
    });

    const gold = new Set<number>();

    // One lookup per country played, not per course.
    const coursesByCountry = new Map<
      string,
      { name: string; rows: MemberCoursesPlayedSummaryDTO[] }
    >();
    coursesPlayed.forEach((row) => {
      if (row.countryId == null || row.countryName == null) {
        return;
      }
      const key = row.countryId.toLowerCase();
      const entry = coursesByCountry.get(key) ?? {
        name: row.countryName,
        rows: [],
      };
      entry.rows.push(row);
      coursesByCountry.set(key, entry);
    });
    coursesByCountry.forEach(({ name, rows }) => {
      const named = byName.get(normalise(name));
      if (named != null) {
        gold.add(named);
        return;
      }
      // A few courses are enough: the first one that lands inside a shape settles it.
      for (const row of rows.slice(0, 5)) {
        const point = pointOf(row);
        const index = point != null ? countryAt(countries, boxes, point) : -1;
        if (index >= 0) {
          gold.add(index);
          return;
        }
      }
    });

    // Nothing played yet: the first home course's country, if it can be placed.
    let homePoint: Point | null = null;
    let isHome = false;
    if (gold.size === 0 && homeCourses.length > 0) {
      homePoint = pointOf(homeCourses[0]);
      if (homePoint != null) {
        const index = countryAt(countries, boxes, homePoint);
        if (index >= 0) {
          gold.add(index);
          isHome = true;
        }
      }
    }

    const goldFeatures = Array.from(gold).map((index) => countries[index]);

    // What to fit: the gold countries, or the home course, or (nothing at all) the whole world.
    const focus: FeatureCollection = {
      type: 'FeatureCollection',
      features:
        goldFeatures.length > 0
          ? goldFeatures.map(mainland)
          : homePoint != null
            ? [
                {
                  type: 'Feature',
                  properties: {},
                  geometry: {
                    type: 'Point',
                    coordinates: [homePoint.longitude, homePoint.latitude],
                  },
                },
              ]
            : countries,
    };
    const centre = geoCentroid(focus);
    if (goldFeatures.length > 0 || homePoint != null) {
      focus.features = [
        ...focus.features,
        {
          type: 'Feature',
          properties: {},
          geometry: geoCircle()
            .center(centre)
            .radius(MIN_VIEW_DEGREES / 2)(),
        },
      ];
    }

    // Turned so the focus sits in the middle. Without it, a member in New Zealand would get New
    // Zealand stretched at the right-hand edge of a map centred on Greenwich.
    const projection = geoNaturalEarth1()
      .rotate([-centre[0], 0])
      .fitExtent(
        [
          [PADDING, PADDING],
          [WIDTH - PADDING, HEIGHT - PADDING],
        ],
        focus,
      )
      .clipExtent([
        [0, 0],
        [WIDTH, HEIGHT],
      ]);
    const path = geoPath(projection);

    return {
      paths: countries.map((country, index) => ({
        key: `${country.id ?? index}`,
        d: path(country) ?? '',
        isGold: gold.has(index),
      })),
      goldCount: gold.size,
      showsHome: isHome,
    };
  }, [countries, coursesPlayed, homeCourses]);

  const label =
    map == null
      ? 'My golf map'
      : map.showsHome
        ? 'Your home course country. Open the map.'
        : `${map.goldCount} ${map.goldCount === 1 ? 'country' : 'countries'} played. Open your played courses on the map.`;

  return (
    <Link
      to="/explore?list=played"
      aria-label={label}
      // Dark in both themes, like Explore and the dashboard map: a map is a map surface, and gold
      // reads on charcoal where it washes out on white.
      className="group block overflow-hidden rounded-lg bg-secondary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 dark:focus-visible:ring-offset-secondary"
    >
      {map != null ? (
        <svg
          viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
          className="block h-auto w-full"
          aria-hidden="true"
        >
          {map.paths.map((country) =>
            country.d ? (
              <path
                key={country.key}
                d={country.d}
                className={
                  country.isGold
                    ? 'fill-primary stroke-secondary/40'
                    : 'fill-charcoal-light stroke-white/20'
                }
                strokeWidth={0.5}
              />
            ) : null,
          )}
        </svg>
      ) : (
        // Same 2:1 box while the world loads (or if it never does), so nothing below jumps.
        <div className="flex aspect-[2/1] w-full items-center justify-center">
          {failed && (
            <span className="text-sm text-white/60">
              The map could not be loaded.
            </span>
          )}
        </div>
      )}

      {/* Under the picture, not on it: a gold country can be anywhere in the frame, and a button
          laid over a corner hid whole countries (southern Africa, bottom right). */}
      <span className="flex items-center justify-end gap-1 border-t border-white/10 px-3 py-2 text-xs font-semibold text-white/80 transition-colors group-hover:text-primary">
        Open the map <ArrowRight className="h-3.5 w-3.5" />
      </span>
    </Link>
  );
};

export default PlayedCountriesMap;
